/**
 * Calendario de temporadas y ciclo quincenal (secciones 6.1 y 6.2).
 *
 * Una temporada es un trimestre de 13 semanas que se juega en 6 quincenas.
 * Cada quincena abre un lunes a las 00:00 UTC y cierra el domingo de la semana
 * siguiente a las 21:00 UTC: 14 dias justos, siempre en lunes. La semana 13
 * queda libre, entre la ultima quincena y la temporada siguiente.
 *
 * Todo vive en UTC, independientemente de la zona horaria del atleta; la
 * interfaz formatea despues en horario local. La base de datos repite este
 * calculo en `fortnight_start` (migracion 20260101001000): si cambias uno,
 * cambia el otro.
 */

import type { Season } from '@/types/domain';

/** Semanas de una temporada trimestral (RF-TEMP-01). */
export const WEEKS_PER_SEASON = 13;

/** Quincenas completas que caben en esas 13 semanas. */
export const FORTNIGHTS_PER_SEASON = 6;

const DAY_MS = 24 * 60 * 60 * 1000;
const FORTNIGHT_DAYS = 14;

export const FORTNIGHT_CYCLE = {
  /** Lunes 00:00 UTC. */
  OPEN_WEEKDAY: 1,
  OPEN_HOUR_UTC: 0,
  /** Domingo de la segunda semana, 21:00 UTC. */
  DEADLINE_DAY_OFFSET: 13,
  DEADLINE_HOUR_UTC: 21,
  /** Ese mismo domingo, 23:59 UTC. */
  PUBLISH_HOUR_UTC: 23,
  PUBLISH_MINUTE_UTC: 59,
} as const;

/** Trimestre natural al que pertenece una fecha. */
export function quarterOf(date: Date): 1 | 2 | 3 | 4 {
  return (Math.floor(date.getUTCMonth() / 3) + 1) as 1 | 2 | 3 | 4;
}

/** Primer dia del trimestre, a las 00:00 UTC. */
export function quarterStart(year: number, quarter: 1 | 2 | 3 | 4): Date {
  return new Date(Date.UTC(year, (quarter - 1) * 3, 1, 0, 0, 0));
}

/** Ultimo instante del trimestre. */
export function quarterEnd(year: number, quarter: 1 | 2 | 3 | 4): Date {
  return new Date(Date.UTC(year, quarter * 3, 1, 0, 0, 0) - 1);
}

/** Lunes 00:00 UTC de la semana que contiene `date`. */
export function weekStart(date: Date): Date {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0),
  );
  // getUTCDay(): 0 = domingo. Se retrocede al lunes anterior.
  const daysSinceMonday = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  return d;
}

/** Cierre de envios de la quincena que empieza en `startsAt`: domingo 21:00 UTC. */
export function fortnightDeadlineFor(startsAt: Date): Date {
  const deadline = weekStart(startsAt);
  deadline.setUTCDate(deadline.getUTCDate() + FORTNIGHT_CYCLE.DEADLINE_DAY_OFFSET);
  deadline.setUTCHours(FORTNIGHT_CYCLE.DEADLINE_HOUR_UTC, 0, 0, 0);
  return deadline;
}

/** Publicacion de resultados de esa quincena: el mismo domingo, 23:59 UTC. */
export function fortnightPublishFor(startsAt: Date): Date {
  const publish = weekStart(startsAt);
  publish.setUTCDate(publish.getUTCDate() + FORTNIGHT_CYCLE.DEADLINE_DAY_OFFSET);
  publish.setUTCHours(FORTNIGHT_CYCLE.PUBLISH_HOUR_UTC, FORTNIGHT_CYCLE.PUBLISH_MINUTE_UTC, 0, 0);
  return publish;
}

/**
 * Lunes en que arranca la primera quincena: el de la semana que contiene el
 * primer dia de la temporada. Un trimestre casi nunca empieza en lunes.
 */
function seasonAnchor(season: Pick<Season, 'start_date'>): Date {
  return weekStart(new Date(season.start_date));
}

/**
 * Quincena 1-6 de la temporada en la que cae una fecha. La semana libre del
 * final cuenta como la 6: no hay septima quincena.
 */
export function seasonFortnightNumber(season: Pick<Season, 'start_date'>, date: Date): number {
  const elapsed = weekStart(date).getTime() - seasonAnchor(season).getTime();
  const index = Math.floor(elapsed / (FORTNIGHT_DAYS * DAY_MS));
  return Math.min(Math.max(index + 1, 1), FORTNIGHTS_PER_SEASON);
}

/** La ultima quincena del trimestre es la de las Termópilas (RF-TEMP-03). */
export function isFinalFortnight(fortnightNumber: number): boolean {
  return fortnightNumber >= FORTNIGHTS_PER_SEASON;
}

/**
 * Estado que corresponde a una competicion publicada segun sus fechas.
 *
 * La base lo impone con un disparador (migracion 20260101001200); el panel lo
 * calcula igual para ensenarlo antes de guardar, y la demo para comportarse
 * como el producto.
 */
export function publishedStatusFor(
  startsAt: string,
  deadline: string,
  now: Date = new Date(),
): 'SCHEDULED' | 'ACTIVE' | 'CLOSED' {
  if (now < new Date(startsAt)) return 'SCHEDULED';
  if (now < new Date(deadline)) return 'ACTIVE';
  return 'CLOSED';
}

export interface Countdown {
  totalMs: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  expired: boolean;
}

/** Cuenta atras hasta una fecha, para las tarjetas de competicion (RF-COMP-06). */
export function countdownTo(target: Date | string, now: Date = new Date()): Countdown {
  const end = typeof target === 'string' ? new Date(target) : target;
  const totalMs = end.getTime() - now.getTime();

  if (!Number.isFinite(totalMs) || totalMs <= 0) {
    return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }

  const seconds = Math.floor(totalMs / 1000);
  return {
    totalMs,
    days: Math.floor(seconds / 86400),
    hours: Math.floor((seconds % 86400) / 3600),
    minutes: Math.floor((seconds % 3600) / 60),
    seconds: seconds % 60,
    expired: false,
  };
}

/** Las 6 quincenas de una temporada con sus fechas de apertura, cierre y publicacion. */
export function seasonFortnights(season: Pick<Season, 'start_date'>): {
  fortnightNumber: number;
  startsAt: Date;
  deadline: Date;
  publishAt: Date;
  isFinalFortnight: boolean;
}[] {
  const anchor = seasonAnchor(season);
  return Array.from({ length: FORTNIGHTS_PER_SEASON }, (_, index) => {
    const startsAt = new Date(anchor);
    startsAt.setUTCDate(startsAt.getUTCDate() + index * FORTNIGHT_DAYS);
    return {
      fortnightNumber: index + 1,
      startsAt,
      deadline: fortnightDeadlineFor(startsAt),
      publishAt: fortnightPublishFor(startsAt),
      isFinalFortnight: isFinalFortnight(index + 1),
    };
  });
}

/** Nombre por defecto de una temporada: `T3 2026` / `Q3 2026`. */
export function defaultSeasonName(quarter: number, year: number): { es: string; en: string } {
  return { es: `T${quarter} ${year}`, en: `Q${quarter} ${year}` };
}
